// src/redux/slices/adminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { showNotification } from './notificationSlice';
import API_ROUTES from "../../config/apiRoutes";

// Fetch admins
export const fetchAdmins = createAsyncThunk(
  'admins/fetchAdmins',
  async (filterObj, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    const requestBody = {
      byAdminId: filterObj?.byAdminId || "",
      byKeyword: filterObj?.byKeyword || "",
      limit: filterObj?.limit || 10,
      currentPage: filterObj?.currentPage || 1
    };
    try {
      const response = await axios.post(
        API_ROUTES.AUTH.GET_ALL,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch admin records"
      );
    }
  }
);

// Create admin
export const createAdmin = createAsyncThunk(
  'admins/createAdmin',
  async (adminData, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.AUTH.CREATE,
        adminData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      dispatch(showNotification({ message: "Admin created successfully!", type: "success" }));
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create admin";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

// Update admin
export const updateAdmin = createAsyncThunk(
  "admins/updateAdmin",
  async ({ id, updatedData }, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.patch(
        API_ROUTES.AUTH.UPDATE(id),
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      dispatch(showNotification({ message: "Admin updated successfully!", type: "success" }));
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update admin";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

// Delete admin
export const deleteAdmin = createAsyncThunk(
  'admins/deleteAdmin',
  async (adminId, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.delete(
        API_ROUTES.AUTH.DELETE(adminId),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      dispatch(showNotification({ message: "Admin deleted successfully!", type: "success" }));
      return adminId;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete admin";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

const adminSlice = createSlice({
  name: 'admins',
  initialState: { admins: [], loading: false, error: null },
  reducers: {
    // Optional: removeAdmin for local deletion (not used if API delete is called)
    removeAdmin(state, action) {
      state.admins = state.admins.filter(admin => admin._id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload.filter(admin => admin.isActive);
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isActive) {
          state.admins.push(action.payload);
        }
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update admin
      .addCase(updateAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.admins.findIndex((admin) => admin._id === action.payload._id);
        if (index !== -1) {
          state.admins[index] = action.payload;
        }
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete admin
      .addCase(deleteAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = state.admins.filter(admin => admin._id !== action.payload);
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { removeAdmin } = adminSlice.actions;
export default adminSlice.reducer;
